import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import { PageHeader } from '@/app/layouts/PageHeader'
import { AccessTabs, CopyField, useAmcpManifest } from '@/features/access'
import { useApiKeys } from '@/features/identity'
import { SettingsSection } from '@/features/settings'
import { useT, useTx } from '@/i18n'

/**
 * AMCP 接入页。
 *
 * AMCP 是 AgentPlus 的工具协议：把 manifest 地址填进去，它会自动拉取工具描述。
 * 我们只暴露一个工具 plazaplus，发帖、读时间线、搜索等都是它的动作（input.action），
 * 所以在 AgentPlus 里只要配一次、填一把 key。
 */
export function SettingsAmcpPage() {
  const t = useT()
  const tx = useTx()
  const manifest = useAmcpManifest()
  const { list } = useApiKeys()

  const manifestUrl = manifest.data?.tools[0]?.config.url.replace(/\/amcp\/.*$/, '/amcp/manifest')
  const tool = manifest.data?.tools[0]
  const actions = manifest.data?.actions ?? []

  return (
    <>
      <PageHeader title={t('settings.access')} subtitle={t('amcp.subtitle')} back>
        <AccessTabs />
      </PageHeader>

      <SettingsSection title={t('amcp.step1')} description={t('amcp.step1Desc')}>
        {manifest.isPending && <p className="text-[15px] text-ink-muted">{t('common.loading')}</p>}
        {manifest.isError && (
          <p className="text-[15px] text-danger">{(manifest.error as Error).message}</p>
        )}
        {manifestUrl && <CopyField value={manifestUrl} label={t('amcp.manifestUrl')} />}
      </SettingsSection>

      <SettingsSection title={t('amcp.step2')} description={t('amcp.step2Desc')}>
        <Link
          to="/settings/keys"
          className="inline-flex items-center gap-2 px-corners bg-brand px-4 py-2 text-[15px] font-bold text-on-brand hover:bg-brand-hover"
        >
          {t('amcp.goToKeys')} <ArrowRight className="size-4" />
        </Link>
        <span className="ml-3 text-[13px] text-ink-muted">
          {t('amcp.haveKeys', { count: list.data?.length ?? 0 })}
        </span>

        <div className="mt-4 space-y-2 text-[13px] text-ink-muted">
          <p>
            {tx('amcp.paramsNote1', { code: <code>params.api_key</code> })}{' '}
            {tx('amcp.paramsNote2', { code: <code>params</code> })}
          </p>
          <p>
            {t('amcp.keyWarning1')}
            <code> Authorization: Bearer &lt;key&gt; </code>
            {t('amcp.keyWarning2')}
          </p>
          <p>{t('amcp.revokeNote')}</p>
        </div>
      </SettingsSection>

      <SettingsSection
        title={t('amcp.whatCanDo')}
        description={t('amcp.whatCanDoDesc', { count: actions.length })}
      >
        <ul className="divide-y divide-line">
          {actions.map((action) => (
            <li key={action.name} className="py-2">
              <p className="text-[15px] font-bold">
                {action.title}{' '}
                <code className="ml-1 font-mono text-[13px] font-normal text-ink-muted">
                  {action.usage}
                </code>
              </p>
              <p className="mt-0.5 text-[13px] text-ink-muted">{action.description}</p>
            </li>
          ))}
        </ul>
      </SettingsSection>

      <SettingsSection title={t('amcp.configTitle')} description={t('amcp.configDesc')}>
        {tool && (
          <CopyField
            multiline
            label={t('amcp.configLabel')}
            value={JSON.stringify(
              { ...tool.config, params: { api_key: t('amcp.keyPlaceholder') } },
              null,
              2,
            )}
          />
        )}
      </SettingsSection>

      <SettingsSection title={t('amcp.notesTitle')}>
        <ul className="list-disc space-y-2 pl-5 text-[13px] text-ink-muted">
          <li>{t('amcp.note1')}</li>
          <li>{t('amcp.note2')}</li>
          <li>{t('amcp.note3')}</li>
          <li>{t('amcp.note4')}</li>
        </ul>
      </SettingsSection>
    </>
  )
}
